/**
 * Mayco Quantity Selector
 *
 * Per-product scope  → pill switcher ("Množstevní sleva")
 * Collection scope   → info table ("Sleva při nákupu z kolekce X")
 * Cart-total scope   → info table ("Sleva z celkového počtu položek v košíku")
 *
 * KEY INSIGHT (Dawn theme):
 * The quantity input is OUTSIDE the <form> element but associated to it via
 * the HTML `form` attribute: <input name="quantity" form="product-form-SECTION_ID">
 * FormData(form) includes it, but form.querySelector() doesn't find it.
 * We must search by the form's ID.
 */

(function () {
  'use strict';

  var _selectedQty = 1;
  var _variantInterval = null;

  /* ── Money formatting ────────────────────────────────────────────────────── */

  function formatMoney(cents, format) {
    if (!format) format = '{{amount_with_comma_separator}} Kč';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.round(cents / 100).toString();
    var amountWithComma = amount.replace('.', ',');
    var amountNoDecimalsWithComma = amountNoDecimals.replace('.', ',');
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
      .replace('{{amount_with_apostrophe_separator}}', amount.replace('.', "'"))
      .replace('{{amount_no_decimals_with_space_separator}}', amountNoDecimals);
  }

  /* ── Styles ──────────────────────────────────────────────────────────────── */

  var STYLE = [
    '.mayco-qty-label{font-size:11px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:#888;margin:0 0 8px 0}',
    '.mayco-qty-wrap{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px}',
    '.mayco-qty-btn{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:10px 16px;min-width:90px;border:2px solid #d9d9d9;border-radius:8px;background:#fff;cursor:pointer;text-align:center;transition:border-color .15s,background .15s;font-family:inherit}',
    '.mayco-qty-btn:hover{border-color:#1a1a1a}',
    '.mayco-qty-btn.is-active{border-color:var(--mayco-active-color,#1a1a1a);background:var(--mayco-active-bg,#f5f5f5)}',
    '.mayco-qty-btn__qty{font-size:14px;font-weight:600;line-height:1.2;color:#1a1a1a}',
    '.mayco-qty-btn__price{font-size:12px;color:#555;margin-top:2px}',
    '.mayco-qty-btn__ribbon{position:absolute;top:-1px;right:-1px;background:var(--mayco-ribbon-color,#e63b2e);color:#fff;font-size:10px;font-weight:700;line-height:1;padding:3px 6px;border-radius:0 6px 0 6px}',
    '.mayco-tiers-box{background:#f5eddd;border-radius:8px;padding:10px 14px;margin-bottom:12px;font-family:inherit}',
    '.mayco-tiers-box__title{font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#e63b2e;margin:0 0 8px 0}',
    '.mayco-tiers-box__pills{display:flex;flex-wrap:nowrap;gap:6px}',
    '.mayco-tiers-box__pill{flex:1 1 0;min-width:0;text-align:center;white-space:nowrap;background:#fff;border:0.5px solid #e0d7c2;border-radius:16px;padding:4px 6px;font-size:12px;color:#1a1a1a}',
    '.mayco-tiers-box__pill b{font-weight:600}',
    '.mayco-tiers-box__pill-disc{color:#e63b2e;font-weight:600}',
    '.mayco-tiers-box__note{font-size:11px;color:#888;margin:8px 0 0}',
    '@media (max-width:400px){.mayco-tiers-box__pills{gap:4px}.mayco-tiers-box__pill{font-size:11px;padding:4px 4px}}',
  ].join('');

  function injectStyles() {
    if (document.getElementById('mayco-qty-styles')) return;
    var s = document.createElement('style');
    s.id = 'mayco-qty-styles';
    s.textContent = STYLE;
    document.head.appendChild(s);
  }

  /* ── Find form ───────────────────────────────────────────────────────────── */

  function getMainForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (!f.closest('dialog') && !f.closest('[data-modal]') && !f.closest('.quick-add')) return f;
    }
    return forms[0] || null;
  }

  /* ── Find quantity input + its quantity-input wrapper ────────────────────── */

  function findQtyElements() {
    var form = getMainForm();
    var formId = form ? form.id : null;
    var qtyInput = null;

    // 1. Form-associated input (Dawn: input is OUTSIDE form, linked via `form` attr)
    if (formId) {
      qtyInput = document.querySelector('input[name="quantity"][form="' + formId + '"]');
    }

    // 2. Inside .product-form__quantity (the wrapping div Dawn uses)
    if (!qtyInput) {
      qtyInput = document.querySelector(
        '.product-form__quantity input[name="quantity"]:not(dialog *):not(.cart-drawer *)'
      );
    }

    // 3. Inside product-page quantity-input (has data-section attr)
    if (!qtyInput) {
      qtyInput = document.querySelector(
        'quantity-input[data-section] input[name="quantity"]'
      );
    }

    // 4. Any quantity input not in dialog/drawer
    if (!qtyInput) {
      var all = document.querySelectorAll('input[name="quantity"]');
      for (var i = 0; i < all.length; i++) {
        if (!all[i].closest('dialog') && !all[i].closest('.cart-drawer') && !all[i].closest('.quick-add')) {
          qtyInput = all[i];
          break;
        }
      }
    }

    var qiEl = qtyInput ? qtyInput.closest('quantity-input') : null;
    return { input: qtyInput, qiEl: qiEl };
  }

  /* ── Set quantity in DOM ─────────────────────────────────────────────────── */

  function setQuantityInDom(qty) {
    _selectedQty = qty;

    var found = findQtyElements();
    var input = found.input;
    var qiEl  = found.qiEl;

    if (!input) return;

    var current = parseInt(input.value, 10) || 1;
    var diff    = qty - current;
    if (diff === 0) return;

    // Strategy 1: use Dawn's +/- buttons (preserves QuantityInput web-component state)
    if (qiEl) {
      var plus  = qiEl.querySelector('button[name="plus"]');
      var minus = qiEl.querySelector('button[name="minus"]');

      if (diff > 0 && plus && !plus.disabled) {
        for (var i = 0; i < diff; i++) plus.click();
        // Verify it worked
        if (parseInt(input.value, 10) === qty) return;
      }
      if (diff < 0 && minus && !minus.disabled) {
        for (var i = 0; i < -diff; i++) minus.click();
        if (parseInt(input.value, 10) === qty) return;
      }
    }

    // Strategy 2: stepUp/stepDown (native — always changes the DOM value)
    if (diff > 0) {
      for (var i = 0; i < diff; i++) input.stepUp();
    } else {
      for (var i = 0; i < -diff; i++) input.stepDown();
    }

    // Dispatch events so the web component updates its UI
    input.dispatchEvent(new Event('input',  { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ── Submit interceptor (safety net) ────────────────────────────────────── */

  function bindSubmitInterceptor() {
    // Bind to document-level capture so we catch all forms including
    // those associated to the quantity input via `form` attr
    if (document._maycoSubmitBound) return;
    document._maycoSubmitBound = true;

    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!form || !form.action) return;
      if (!form.action.includes('/cart/add')) return;

      // Force-set correct quantity on the associated input
      var found = findQtyElements();
      if (found.input) found.input.value = String(_selectedQty);
    }, true); // capture phase
  }

  /* ── Switcher (per-product) ──────────────────────────────────────────────── */

  function renderSwitcher(el, tiers, price, moneyFormat) {
    var label = document.createElement('p');
    label.className = 'mayco-qty-label';
    label.textContent = el.dataset.i18nLabel || 'Množstevní sleva';
    el.appendChild(label);

    var wrap = document.createElement('div');
    wrap.className = 'mayco-qty-wrap';

    function makeBtn(qtyLabel, priceLabel, ribbon) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mayco-qty-btn';

      var qs = document.createElement('span');
      qs.className = 'mayco-qty-btn__qty';
      qs.textContent = qtyLabel;
      btn.appendChild(qs);

      var ps = document.createElement('span');
      ps.className = 'mayco-qty-btn__price';
      ps.textContent = priceLabel;
      btn.appendChild(ps);

      if (ribbon) {
        var rb = document.createElement('span');
        rb.className = 'mayco-qty-btn__ribbon';
        rb.textContent = ribbon;
        btn.appendChild(rb);
      }
      return btn;
    }

    function setActive(activeBtn) {
      wrap.querySelectorAll('.mayco-qty-btn').forEach(function (b) { b.classList.remove('is-active'); });
      activeBtn.classList.add('is-active');
    }

    var single = makeBtn('1 ks', formatMoney(price, moneyFormat) + '/ks', null);
    if (_selectedQty === 1) single.classList.add('is-active');
    single.addEventListener('click', function () { setActive(single); setQuantityInDom(1); });
    wrap.appendChild(single);

    tiers.forEach(function (tier) {
      var discounted = Math.round(price * (1 - tier.discount_pct / 100));
      var btn = makeBtn(
        tier.min_qty + ' ks',
        formatMoney(discounted, moneyFormat) + '/ks',
        '-' + tier.discount_pct + '%'
      );
      if (_selectedQty === tier.min_qty) btn.classList.add('is-active');
      btn.addEventListener('click', function () { setActive(btn); setQuantityInDom(tier.min_qty); });
      wrap.appendChild(btn);
    });

    el.appendChild(wrap);
  }

  /* ── Info table (collection / cart-total) ────────────────────────────────── */

  function renderTable(el, tiers, scope, productConfig) {
    var box = document.createElement('div');
    box.className = 'mayco-tiers-box';

    var title = document.createElement('p');
    title.className = 'mayco-tiers-box__title';
    if (scope === 'collection') {
      var collName = (productConfig && productConfig.collection_title) || 'kolekce';
      var collTpl = el.dataset.i18nCollectionTitle || 'Množstevní sleva pro kolekci __COLL__';
      title.textContent = collTpl.replace('__COLL__', collName);
    } else {
      title.textContent = el.dataset.i18nCartTotalTitle || 'Sleva z počtu položek v košíku';
    }
    box.appendChild(title);

    var pills = document.createElement('div');
    pills.className = 'mayco-tiers-box__pills';
    tiers.forEach(function (tier) {
      var pill = document.createElement('span');
      pill.className = 'mayco-tiers-box__pill';
      var qty = document.createElement('b');
      qty.textContent = tier.min_qty + '+ ks';
      var disc = document.createElement('span');
      disc.className = 'mayco-tiers-box__pill-disc';
      disc.textContent = ' −' + tier.discount_pct + '%';
      pill.appendChild(qty);
      pill.appendChild(disc);
      pills.appendChild(pill);
    });
    box.appendChild(pills);

    if (scope === 'collection') {
      var note = document.createElement('p');
      note.className = 'mayco-tiers-box__note';
      note.textContent = el.dataset.i18nCollectionNote || 'Počítá se napříč produkty této řady se štítkem „Množstevní sleva".';
      box.appendChild(note);
    }

    el.appendChild(box);
  }

  /* ── Core init ───────────────────────────────────────────────────────────── */

  function init() {
    var el = document.getElementById('mayco-qty-selector');
    if (!el) return;

    var globalConfig, productConfig;
    try { globalConfig = JSON.parse(el.dataset.global || 'null'); } catch (e) { globalConfig = null; }
    try { productConfig = JSON.parse(el.dataset.product || 'null'); } catch (e) { productConfig = null; }

    if (!globalConfig || !globalConfig.enabled) return;
    if (productConfig && productConfig.enabled === false) return;

    var tiers = (productConfig && productConfig.tiers && productConfig.tiers.length)
      ? productConfig.tiers
      : globalConfig.tiers;
    if (!tiers || !tiers.length) return;

    tiers = tiers.slice().sort(function (a, b) { return a.min_qty - b.min_qty; });

    var price       = parseInt(el.dataset.price, 10) || 0;
    var moneyFormat = el.dataset.moneyFormat || '{{amount_with_comma_separator}} Kč';
    var scope       = (productConfig && productConfig.scope)
                      || globalConfig.default_scope
                      || globalConfig.mode
                      || 'per_product';

    injectStyles();

    if (scope === 'per_product') {
      renderSwitcher(el, tiers, price, moneyFormat);
    } else {
      renderTable(el, tiers, scope, productConfig);
    }

    bindSubmitInterceptor();
    watchVariant(el);
  }

  /* ── Variant change detection ────────────────────────────────────────────── */

  function watchVariant(selectorEl) {
    if (_variantInterval) return;

    var handle = selectorEl.dataset.handle;
    if (!handle) return;

    var form = getMainForm();
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var lastId = variantInput ? variantInput.value : null;

    // Poll for variant ID changes (MutationObserver doesn't fire on .value =)
    _variantInterval = setInterval(function () {
      if (!variantInput) return;
      if (variantInput.value === lastId) return;
      lastId = variantInput.value;
      fetchVariantPrice(handle, lastId, selectorEl);
    }, 300);

    // Also catch URL ?variant= changes
    window.addEventListener('popstate', function () {
      var vid = new URLSearchParams(window.location.search).get('variant');
      if (vid && vid !== lastId) {
        lastId = vid;
        fetchVariantPrice(handle, vid, selectorEl);
      }
    });
  }

  function fetchVariantPrice(handle, variantId, selectorEl) {
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var variant = product.variants.find(function (v) {
          return String(v.id) === String(variantId);
        });
        if (!variant) return;
        _selectedQty = 1;
        selectorEl.dataset.price = variant.price;
        selectorEl.innerHTML = '';
        init();
      })
      .catch(function () {});
  }

  /* ── Bootstrap ───────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
